import { useEffect } from "react";
import { motion } from "framer-motion";
import { Briefcase, Headphones, Shield, TrendingUp, Users } from "lucide-react";
import SimCollectLogo, { SimCollectLogoIcon } from "../logo/SimCollectLogo";

const OPS_FEATURES = [
  {
    icon: Briefcase,
    title: "Case queue",
    detail: "Assign and track recovery cases across your team",
  },
  {
    icon: Headphones,
    title: "Contact centre",
    detail: "Log calls, outcomes, and payment commitments",
  },
  {
    icon: Users,
    title: "Team oversight",
    detail: "Monitor collector workload and SLA performance",
  },
];

const OPS_STATS = [
  { value: "732", label: "Active cases" },
  { value: "24", label: "Agents online" },
  { value: "156", label: "Contacts today" },
];

const WORKFLOW_STEPS = [
  { icon: Briefcase, label: "Assign cases" },
  { icon: Headphones, label: "Contact debtor" },
  { icon: TrendingUp, label: "Track recovery" },
];

function OpsWorkflowBridge() {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.96 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.55, ease: "easeOut", delay: 0.15 }}
      className="relative hidden w-full max-w-[200px] shrink-0 flex-col items-center px-1 xl:flex 2xl:max-w-[240px]"
      aria-hidden="true"
    >
      <div className="pointer-events-none absolute left-0 top-1/2 hidden h-px w-6 -translate-y-1/2 bg-gradient-to-r from-transparent to-accent/40 2xl:block" />
      <div className="pointer-events-none absolute right-0 top-1/2 hidden h-px w-6 -translate-y-1/2 bg-gradient-to-l from-transparent to-accent/40 2xl:block" />

      <div className="relative">
        <div className="absolute inset-0 scale-150 rounded-full bg-accent/15 blur-3xl" />
        <div className="relative flex h-24 w-24 items-center justify-center rounded-full border border-primary/10 bg-white shadow-[0_0_40px_rgba(229,184,11,0.15)] 2xl:h-28 2xl:w-28">
          <SimCollectLogoIcon size={64} className="relative z-10 2xl:hidden" />
          <SimCollectLogoIcon size={80} className="relative z-10 hidden 2xl:block" />
        </div>
      </div>

      <p className="mt-4 text-center text-[10px] font-bold uppercase tracking-[0.22em] text-primary/40 2xl:mt-5">
        Recovery workflow
      </p>

      <div className="mt-3 w-full space-y-0 2xl:mt-4">
        {WORKFLOW_STEPS.map((step, i) => (
          <div key={step.label} className="flex flex-col items-center">
            <div className="flex w-full items-center gap-2 rounded-xl border border-primary/10 bg-white/80 px-2.5 py-2 shadow-sm 2xl:gap-2.5 2xl:px-3 2xl:py-2.5">
              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-accent/15 text-accent 2xl:h-8 2xl:w-8">
                <step.icon className="h-3.5 w-3.5 2xl:h-4 2xl:w-4" strokeWidth={2.25} />
              </span>
              <span className="text-[11px] font-semibold text-primary/80 2xl:text-xs">{step.label}</span>
            </div>
            {i < WORKFLOW_STEPS.length - 1 && (
              <div className="flex h-4 flex-col items-center justify-center 2xl:h-5">
                <span className="h-full w-px bg-gradient-to-b from-accent/50 via-accent/25 to-accent/50" />
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="mt-4 w-full rounded-xl border border-accent/25 bg-accent/10 px-3 py-2 text-center 2xl:mt-5 2xl:py-2.5">
        <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-accent-dark/80">Live today</p>
        <p className="mt-1 text-sm font-bold tabular-nums text-primary">156 contacts logged</p>
      </div>
    </motion.div>
  );
}

function StepIndicator({ steps, currentStep }) {
  if (!steps?.length) return null;

  return (
    <div className="mb-4 flex items-center gap-3">
      {steps.map((step, i) => {
        const stepNum = i + 1;
        const isActive = stepNum === currentStep;
        const isDone = stepNum < currentStep;

        return (
          <div key={step.label} className="flex items-center gap-3">
            {i > 0 && (
              <div className={`h-px w-8 sm:w-10 ${isDone ? "bg-accent" : "bg-primary/15"}`} />
            )}
            <div className="flex items-center gap-2">
              <span
                className={`
                  flex h-6 w-6 items-center justify-center rounded-full text-[10px] font-bold
                  transition-colors duration-300
                  ${isActive ? "bg-accent text-primary" : isDone ? "bg-accent/20 text-accent-dark" : "bg-primary/8 text-primary/35"}
                `}
              >
                {isDone ? "✓" : stepNum}
              </span>
              <span
                className={`hidden text-[10px] font-medium uppercase tracking-wider sm:block ${
                  isActive ? "text-primary" : "text-primary/35"
                }`}
              >
                {step.label}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function AuthLayout({
  children,
  title,
  subtitle,
  badge = "Operations Portal",
  steps,
  currentStep,
}) {
  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const prevHtml = html.style.overflow;
    const prevBody = body.style.overflow;

    html.style.overflow = "hidden";
    body.style.overflow = "hidden";

    return () => {
      html.style.overflow = prevHtml;
      body.style.overflow = prevBody;
    };
  }, []);

  return (
    <div className="relative flex h-dvh max-h-dvh flex-col overflow-hidden bg-[#faf6ef]">
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.35]"
        style={{
          backgroundImage: `
            linear-gradient(rgba(59,47,41,0.06) 1px, transparent 1px),
            linear-gradient(90deg, rgba(59,47,41,0.06) 1px, transparent 1px)
          `,
          backgroundSize: "44px 44px",
        }}
      />

      <div className="pointer-events-none absolute left-1/2 top-0 h-[500px] w-[800px] -translate-x-1/2 rounded-full bg-collect/20 blur-[120px]" />
      <div className="pointer-events-none absolute bottom-0 right-0 h-80 w-80 rounded-full bg-accent/20 blur-[100px]" />

      <header className="auth-header relative z-30 shrink-0 px-4 py-2 sm:px-6 sm:py-3 lg:px-10">
        <div className="mx-auto w-full max-w-8xl">
          <div className="origin-left scale-[0.8] sm:scale-90">
            <SimCollectLogo theme="light" iconSize={44} variant="wordmark" />
          </div>
        </div>
      </header>

      <main className="auth-main-scroll relative z-10 min-h-0 flex-1 overflow-y-auto overscroll-contain">
        <div className="auth-content mx-auto flex w-full max-w-8xl flex-col items-center gap-6 px-4 py-4 sm:gap-8 sm:px-6 sm:py-6 md:gap-10 lg:flex-row lg:items-center lg:justify-between lg:gap-8 lg:px-10 lg:py-4 xl:gap-10 2xl:gap-12">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, ease: "easeOut" }}
            className="relative z-20 w-full max-w-[480px] shrink-0 lg:max-w-[420px] xl:max-w-[460px]"
          >
            <div className="overflow-hidden rounded-xl border border-primary/10 bg-[#FDFCFA] shadow-[0_24px_48px_-16px_rgba(59,47,41,0.18)] ring-1 ring-primary/5 sm:rounded-2xl">
              <div className="h-1 bg-gradient-to-r from-collect via-accent to-collect" />

              <div className="auth-card-body px-5 py-4 sm:px-7 sm:py-6">
                {badge && (
                  <span className="auth-card-badge inline-flex items-center gap-1.5 rounded-full bg-primary/5 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.2em] text-primary/60 ring-1 ring-primary/10">
                    <Shield className="h-3 w-3 text-accent" />
                    {badge}
                  </span>
                )}

                <StepIndicator steps={steps} currentStep={currentStep} />

                <div className={`auth-card-heading ${steps?.length ? "mt-2" : "mt-3"}`}>
                  <h2 className="text-lg font-bold leading-tight tracking-tight text-primary sm:text-xl sm:leading-tight lg:text-[1.35rem]">
                    {title}
                  </h2>
                  {subtitle && (
                    <p className="auth-card-subtitle mt-1.5 text-sm leading-snug text-primary/50">{subtitle}</p>
                  )}
                </div>

                <div className="auth-card-divider my-4 h-px bg-gradient-to-r from-transparent via-primary/10 to-transparent" />

                {children}
              </div>
            </div>
          </motion.div>

          <OpsWorkflowBridge />

          <motion.aside
            initial={{ opacity: 0, x: 24 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, ease: "easeOut", delay: 0.08 }}
            className="hidden w-full shrink-0 md:block md:max-w-[440px] lg:max-w-[340px] lg:text-left xl:max-w-md"
          >
            <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-accent sm:text-xs">
              SimCollect Operations
            </p>
            <h1 className="mt-2 text-2xl font-bold leading-tight text-primary sm:mt-2.5 sm:text-[1.7rem] xl:text-3xl">
              Run collection operations from one{" "}
              <span className="text-accent-dark">command console</span>
            </h1>
            <p className="mt-2 text-sm leading-relaxed text-primary/55">
              SimCollect staff — assign cases, log contacts, and monitor recovery performance.
            </p>

            <ul className="mt-3 hidden space-y-2 lg:mt-4 lg:block">
              {OPS_FEATURES.map((item, i) => (
                <motion.li
                  key={item.title}
                  initial={{ opacity: 0, x: 12 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.2 + i * 0.08 }}
                  className="flex items-center gap-3 rounded-xl border border-primary/10 bg-white/70 px-3.5 py-2.5 shadow-sm"
                >
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-accent/15 text-accent">
                    <item.icon className="h-3.5 w-3.5" strokeWidth={2.25} />
                  </span>
                  <div>
                    <p className="text-sm font-semibold text-primary">{item.title}</p>
                    <p className="mt-0.5 text-[11px] leading-snug text-primary/45">{item.detail}</p>
                  </div>
                </motion.li>
              ))}
            </ul>

            <div className="mt-3 grid grid-cols-3 gap-2 rounded-xl border border-primary/10 bg-white/70 p-2.5 shadow-sm lg:mt-4">
              {OPS_STATS.map((stat) => (
                <div key={stat.label} className="text-center">
                  <p className="text-base font-bold tabular-nums text-primary">{stat.value}</p>
                  <p className="mt-0.5 text-[10px] leading-tight text-primary/40">{stat.label}</p>
                </div>
              ))}
            </div>
          </motion.aside>
        </div>
      </main>

      <footer className="auth-footer relative z-10 shrink-0 px-4 py-2 sm:px-6 lg:px-10">
        <p className="mx-auto w-full max-w-8xl text-center text-[10px] text-primary/35 sm:text-xs lg:text-left">
          © {new Date().getFullYear()} SimCollect · Authorized personnel only
        </p>
      </footer>
    </div>
  );
}
